/**
 * API Service for LAMED
 *
 * This module provides a typed API client for communicating with the LAMED backend.
 * All functions include proper TypeScript types for requests and responses.
 */

import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { reportNetworkError } from './errorReporting';
import type {
  Item,
  CreateItemRequest,
  UpdateItemRequest,
  HealthResponse,
  StatsResponse,
  GetItemsParams,
  APIError
} from '@/types/api';

// ============================================================
// API Client Configuration
// ============================================================

/**
 * Base API client instance with default configuration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================
// Request Interceptor
// ============================================================

apiClient.interceptors.request.use(
  (config) => {
    // Add auth token from localStorage if available
    const token = localStorage.getItem('lamed_auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add timestamp to all requests for debugging
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, config.data || '');
    return config;
  },
  (error: AxiosError) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// ============================================================
// Response Interceptor
// ============================================================

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`[API] Response ${response.status}:`, response.data);
    return response;
  },
  async (error: AxiosError<APIError>) => {
    // Report error to audit system
    await reportNetworkError(error, {
      url: error.config?.url,
      method: error.config?.method,
      requestData: error.config?.data,
      responseData: error.response?.data,
      statusCode: error.response?.status
    });

    // Handle different error scenarios
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.detail || error.message;

      console.error(`[API] Error ${status}:`, message);

      // Customize error messages based on status code
      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          console.warn('[API] Authentication expired, logging out...');

          // Clear auth data from localStorage
          localStorage.removeItem('lamed_auth_token');
          localStorage.removeItem('lamed_auth_user');

          // Redirect to login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login?session_expired=true';
          }

          throw new Error('Session expired. Please log in again.');

        case 400:
          throw new Error(`Bad Request: ${message}`);
        case 403:
          throw new Error(`Forbidden: ${message}`);
        case 404:
          throw new Error(`Not Found: ${message}`);
        case 422:
          throw new Error(`Validation Error: ${message}`);
        case 500:
          throw new Error('Server Error: Please try again later');
        case 503:
          throw new Error('Service Unavailable: Server is temporarily down');
        default:
          throw new Error(message);
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('[API] No response:', error.request);
      throw new Error('Network Error: Unable to reach server');
    } else {
      // Error in request configuration
      console.error('[API] Error:', error.message);
      throw new Error(`Request Error: ${error.message}`);
    }
  }
);

// ============================================================
// Health & Status Endpoints
// ============================================================

/**
 * Check API health status
 * @returns Health check response with database status
 */
export const getHealth = async (): Promise<HealthResponse> => {
  const response = await apiClient.get<HealthResponse>('/health');
  return response.data;
};

/**
 * Get database statistics
 * @returns Statistics including item counts, categories, and activity
 */
export const getStats = async (): Promise<StatsResponse> => {
  const response = await apiClient.get<StatsResponse>('/stats');
  return response.data;
};

// ============================================================
// Item CRUD Endpoints
// ============================================================

/**
 * Get a list of items with optional filtering
 * @param params Query parameters for filtering and pagination
 * @returns Array of items matching the criteria
 */
export const getItems = async (params?: GetItemsParams): Promise<Item[]> => {
  const response = await apiClient.get<Item[]>('/items', { params });
  return response.data;
};

/**
 * Get a single item by ID
 * @param id Item ID
 * @returns The requested item
 * @throws Error if item not found (404)
 */
export const getItem = async (id: number): Promise<Item> => {
  const response = await apiClient.get<Item>(`/items/${id}`);
  return response.data;
};

/**
 * Create a new item
 * @param data Item data to create
 * @returns The created item with generated ID and timestamps
 */
export const createItem = async (data: CreateItemRequest): Promise<Item> => {
  const response = await apiClient.post<Item>('/items', data);
  return response.data;
};

/**
 * Update an existing item
 * @param id Item ID to update
 * @param data Fields to update (all optional)
 * @returns The updated item
 * @throws Error if item not found (404)
 */
export const updateItem = async (id: number, data: UpdateItemRequest): Promise<Item> => {
  const response = await apiClient.put<Item>(`/items/${id}`, data);
  return response.data;
};

/**
 * Delete an item
 * @param id Item ID to delete
 * @throws Error if item not found (404)
 */
export const deleteItem = async (id: number): Promise<void> => {
  await apiClient.delete(`/items/${id}`);
};

// ============================================================
// Export API Client (for advanced use cases)
// ============================================================

export default apiClient;
